import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Layout, Menu, Space, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useEffect, useState } from 'react';
import { Link, Outlet, useMatches, useNavigate, useNavigation } from 'react-router-dom';

import { NotificationCenter } from '../features/exports/notification-center.js';
import { api } from '../shared/api-client.js';
import { businessCodeDisplay } from '../shared/business-code-display.js';
import { UnsavedChangesProvider } from '../shared/unsaved-changes.js';
import { systemVersionLabel } from './app-version.js';
import { RouteNavigationLoading } from './route-loading.js';
import { AppBreadcrumb, AppScrollRestoration } from './route-navigation-components.js';
import { navigationKeyFromMatches } from './route-navigation.js';
import { useSession } from './session-context.js';

const SIDER_MEDIA_QUERY = '(max-width: 960px)';
const SIDER_SESSION_KEY = 'badminton:sider-collapsed';

export function AuthenticatedLayout() {
  const { message: messageApi } = AntdApp.useApp();
  const session = useSession();
  const matches = useMatches();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const [manualCollapsed, setManualCollapsed] = useState<boolean | undefined>(readSiderPreference);
  const [narrowViewport, setNarrowViewport] = useState(matchesNarrowViewport);
  const logout = useMutation({
    mutationFn: api.logout,
    onError: (error) => {
      void messageApi.error(error instanceof Error ? error.message : '退出失败，请稍后重试');
    },
    onSuccess: () => {
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });
  const collapsed = manualCollapsed ?? narrowViewport;
  const navigationItem = (key: string, label: string, mark: string) => ({
    key,
    icon: (
      <span className="navigation-mark" aria-hidden="true">
        {mark}
      </span>
    ),
    label: <Link to={key}>{label}</Link>,
    title: label,
  });
  const navigationItems: NonNullable<MenuProps['items']> = [
    navigationItem('/dashboard', '工作台', '台'),
    ...(session.role === 'ADMIN'
      ? [navigationItem('/purchases/activity', '新增购课记录', '购')]
      : []),
    ...(session.capabilities.includes('employee.read')
      ? [navigationItem('/employees', '员工管理', '员')]
      : []),
    ...(session.capabilities.includes('trial.read')
      ? [navigationItem('/trials', '体验课', '体')]
      : []),
    ...(session.capabilities.includes('student.read')
      ? [navigationItem('/students', '学员管理', '学')]
      : []),
    ...(session.capabilities.includes('packageTemplate.read')
      ? [navigationItem('/package-templates', '课包管理', '包')]
      : []),
    ...(session.capabilities.includes('commissionPlan.read')
      ? [navigationItem('/commission-plans', '分成公式与计划', '计')]
      : []),
    ...(session.capabilities.includes('consumption.read')
      ? [navigationItem('/consumptions', '课销', '消')]
      : []),
    ...(session.capabilities.includes('analytics.read')
      ? [navigationItem('/analytics', '数据分析', '析')]
      : []),
    ...(session.capabilities.includes('dailyTask.template.manage')
      ? [navigationItem('/daily-task-templates', '任务模板', '任')]
      : []),
    ...(session.capabilities.includes('dailyTask.review')
      ? [navigationItem('/daily-task-reviews', '待办审核', '核')]
      : []),
    ...(session.capabilities.includes('dailyTask.evidence.read')
      ? [navigationItem('/daily-task-evidence', '完成依据', '据')]
      : []),
    ...(session.capabilities.includes('commission.read') ||
    session.capabilities.includes('salesCommission.readAll')
      ? [navigationItem('/commissions', '员工分成', '分')]
      : []),
    ...(session.capabilities.includes('salesCommission.readOwn') &&
    !session.capabilities.includes('salesCommission.readAll')
      ? [navigationItem('/sales-commissions', '我的分成', '销')]
      : []),
    ...(session.capabilities.includes('consumption.export') ||
    session.capabilities.includes('commission.export') ||
    session.capabilities.includes('salesCommission.export')
      ? [navigationItem('/exports', '导出任务', '出')]
      : []),
    ...(session.role === 'ADMIN' || session.role === 'VENUE_MANAGER'
      ? [navigationItem('/audits', '审计与对账', '审')]
      : []),
  ];
  const selected = navigationKeyFromMatches(matches);
  const configuredVersion: unknown = import.meta.env.VITE_APP_VERSION;
  const systemVersion = systemVersionLabel(
    typeof configuredVersion === 'string' ? configuredVersion : undefined,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(SIDER_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setNarrowViewport(event.matches);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <Layout className="app-shell">
      <Layout.Sider
        className={collapsed ? 'app-sider app-sider-collapsed' : 'app-sider'}
        collapsed={collapsed}
        collapsedWidth={80}
        trigger={null}
        width={232}
      >
        <div className="app-sider-content">
          <Link className="brand" to="/dashboard" aria-label="羽毛球教务系统工作台">
            <span className="brand-mark" aria-hidden="true">
              羽
            </span>
            <span className="brand-copy">
              <strong>场馆教务</strong>
              <small>BADMINTON OPS</small>
            </span>
          </Link>
          <Button
            aria-label={collapsed ? '展开导航' : '收起导航'}
            className="sider-collapse-button"
            type="text"
            onClick={() => {
              const next = !collapsed;
              setManualCollapsed(next);
              writeSiderPreference(next);
            }}
          >
            {collapsed ? '展开' : '收起导航'}
          </Button>
          <nav className="app-navigation" aria-label="主导航">
            <Menu
              items={navigationItems}
              mode="inline"
              selectedKeys={selected ? [selected] : []}
              theme="dark"
            />
          </nav>
          {collapsed ? null : (
            <div
              aria-label={`系统版本 ${systemVersion.full}`}
              className="sider-footnote"
              title={systemVersion.full}
            >
              版本 {systemVersion.short}
            </div>
          )}
        </div>
      </Layout.Sider>
      <Layout className="app-main-layout">
        <Layout.Header className="app-header">
          <Typography.Text className="venue-name">搜羽羽毛球馆(武侯馆)</Typography.Text>
          <Space className="app-header-actions" size={18} wrap>
            <NotificationCenter />
            <Tag>{businessCodeDisplay('userRole', session.role).label}</Tag>
            <Link to="/account/password">{session.name}</Link>
            <Button
              loading={logout.isPending}
              type="text"
              onClick={() => {
                logout.mutate();
              }}
            >
              退出
            </Button>
          </Space>
        </Layout.Header>
        {navigation.state !== 'idle' ? <RouteNavigationLoading /> : null}
        <Layout.Content className="app-content">
          <AppBreadcrumb />
          <UnsavedChangesProvider>
            <Outlet />
          </UnsavedChangesProvider>
          <AppScrollRestoration />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

function matchesNarrowViewport(): boolean {
  return window.matchMedia(SIDER_MEDIA_QUERY).matches;
}

function readSiderPreference(): boolean | undefined {
  try {
    const value = window.sessionStorage.getItem(SIDER_SESSION_KEY);
    if (value === 'collapsed') return true;
    if (value === 'expanded') return false;
  } catch {
    return undefined;
  }
  return undefined;
}

function writeSiderPreference(collapsed: boolean): void {
  try {
    window.sessionStorage.setItem(SIDER_SESSION_KEY, collapsed ? 'collapsed' : 'expanded');
  } catch {
    // Session storage can be unavailable in hardened browser contexts.
  }
}
